
import { ConfigurationStatus, UpdateStatusDataType, UpdateStatusEvent, UpdateStatusEventType } from "@/api/dtos";
import { AppSettings } from "@/AppSettings";
import IsLoading from "@/components/layouts/IsLoading";
import Show from "@/components/layouts/Show";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import TrialContext from "@/contexts/data/TrialContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, CircularProgress, Paper, Typography, styled } from "@mui/material";
import { FC, useCallback, useContext, useMemo, useState } from "react";
import { Link, Outlet, UNSAFE_RouteContext, useMatches, useNavigate, useParams } from "react-router-dom";

import OpenClinicaCrfLink from "@/components/buttons/OpenClinicaCrfLink";
import SourceDataEventDeleteDialog from "@/components/dialogs/SourceDataEventDeleteDialog";
import SourceDataEventEditDialog from "@/components/dialogs/SourceDataEventEditDialog";
import SourceDataFormSettingsDialog from "@/components/dialogs/SourceDataFormSettingsDialog";
import SourceDataNavigation from "@/components/forms/forms/source-data/SourceDataNavigation";
import { MessagesView } from "@/components/layouts/Messages";
import SetLinks from "@/components/layouts/SetLinks";
import { StatusListItemTooltip } from "@/components/lists/status/StatusListStats";
import ConfigurationSourceDataContext from "@/contexts/data/ConfigurationSourceDataContext";
import CoordinatingGroupContext from "@/contexts/data/CoordinatingGroupContext";
import MasterGroupContext from "@/contexts/data/MasterGroupContext";
import MetadataContext from "@/contexts/data/MetadataContext";
import ParticipantContext from "@/contexts/data/ParticipantContext";
import ParticipantNotesAndQueriesContext from "@/contexts/data/ParticipantNotesAndQueriesContext";
import ParticipantSourceDataEventStatusesContext from "@/contexts/data/ParticipantSourceDataEventStatusesContext";
import ParticipantSourceDataFindingsContext from "@/contexts/data/ParticipantSourceDataFindingsContext";
import ParticipantSourceDataFormStatusesContext from "@/contexts/data/ParticipantSourceDataFormStatusesContext";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import SourceDataFormContext from "@/contexts/form/SourceDataFormContext";
import SourceDataFormSettingsContext, { DefaultSourceDataFormSettings, ISourceDataFormSettings } from "@/contexts/form/SourceDataFormSettingsContext";
import { ParticipantInstitutionCreateLink, ParticipantTrialCreateLink } from "@/contexts/layouts/LinkContext";
import { LoadingContext } from "@/contexts/layouts/LoadingContext";
import { ShowLayoutContext } from "@/contexts/layouts/ShowLayoutContext";
import useDeleteSourceDataEvent from "@/hooks/data/useCanDeleteSourceDataEvent";
import useConfigurationSourceDataMap from "@/hooks/data/useConfigurationSourceDataMap";
import useParticipantNotesAndQueries from "@/hooks/data/useParticipantNotesAndQueries";
import useParticipantSourceDataAllocation from "@/hooks/data/useParticipantSourceDataAllocation";
import useParticipantSourceDataEventStatuses from "@/hooks/data/useParticipantSourceDataEventStatuses";
import useParticipantSourceDataFindings from "@/hooks/data/useParticipantSourceDataFindings";
import useParticipantSourceDataFormStatuses from "@/hooks/data/useParticipantSourceDataFormStatuses";
import useSourceData from "@/hooks/data/useSourceData";
import useStatusUpdate from "@/hooks/data/useStatusUpdate";
import useEvent from "@/hooks/form/source-data/useEvent";
import useForm from "@/hooks/form/source-data/useForm";
import { LoadingButton } from "@mui/lab";
import { debounce } from "lodash-es";
import { DateTime } from 'luxon';
import { useLocalStorage } from "react-use";
import OpenClinicaEventLink from "@/components/buttons/OpenClinicaEventLink";
import OpenClinicaParticipantLink from "@/components/buttons/OpenClinicaParticipantLink";

const TitleBox = styled(Box)(({theme}) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
}))

const ParticipantSourceDataMessages: FC = () => {
    const { data: trial } = useContext(TrialContext);
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: coordinatingGroup } = useContext(CoordinatingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: participant } = useContext(ParticipantContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    const { data: configuration } = useContext(ConfigurationSourceDataContext);
    const { data: sourceData } = useContext(SourceDataContext);

    const { studyEventOid, studyEventRepeat, caseReportFormOid, itemName } = useParams();

    const event = useEvent(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        +studyEventRepeat!
    )

    const form = useForm(
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        studyEventOid,
        +studyEventRepeat!,
        caseReportFormOid
    )
    
    return (
        <>
            <MessagesView
                entityName="Allocation"
                storageKey={`Alert-Configuration-${participant?.id}-${configuration?.configuration?.id}-${configuration?.configuration?.modifiedDate}`}
                alertSuccess={configuration?.configuration?.alertSuccess}
                alertInformation={configuration?.configuration?.alertInformation}
                alertWarning={configuration?.configuration?.alertWarning}
                alertError={configuration?.configuration?.alertError}
            />
            {
                !!event && (
                    <MessagesView
                        entityName="Event"
                        storageKey={`Alert-Event-${participant?.id}-${event.studyEventOid}-${configuration?.configuration?.id}-${configuration?.configuration?.modifiedDate}`}
                        alertSuccess={event.configuration?.alertSuccess}
                        alertInformation={event.configuration?.alertInformation}
                        alertWarning={event.configuration?.alertWarning}
                        alertError={event.configuration?.alertError}
                    />
                )
            }
            {
                !!form && (
                    <MessagesView
                        entityName="Form"
                        storageKey={`Alert-Form-${participant?.id}-${form.studyEventOid}-${form.caseReportFormOid}-${configuration?.configuration?.id}-${configuration?.configuration?.modifiedDate}`}
                        alertSuccess={form.configuration?.alertSuccess}
                        alertInformation={form.configuration?.alertInformation}
                        alertWarning={form.configuration?.alertWarning}
                        alertError={form.configuration?.alertError}
                    />
                )
            }
            {
                !!form?.formVersion && (
                    <MessagesView
                        entityName="Form Version"
                        storageKey={`Alert-Form-Version-${participant?.id}-${form.studyEventOid}-${form.caseReportFormOid}-${form.formVersion.caseReportFormVersionOid}-${configuration?.configuration?.id}-${configuration?.configuration?.modifiedDate}`}
                        alertSuccess={form.formVersion.configuration?.alertSuccess}
                        alertInformation={form.formVersion.configuration?.alertInformation}
                        alertWarning={form.formVersion.configuration?.alertWarning}
                        alertError={form.formVersion.configuration?.alertError}
                    />
                )
            }
        </>
    );
}

const ParticipantSourceData: FC = () => {
    const { data: trial } = useContext(TrialContext);
    const { data: participant } = useContext(ParticipantContext);

    const { data: allocation, loading: allocationLoading, validating: allocationValidating } = useParticipantSourceDataAllocation(trial?.id && participant?.id ? { trialId: trial.id, participantId: participant.id } : undefined);
    const configurationContext = useConfigurationSourceDataMap(trial?.id, allocation?.configurationSourceDataId ? allocation?.configurationSourceDataId : undefined);

    const sourceDataContext = useSourceData(trial?.id, participant?.id);

    const { data: sourceData, loading: sourceDataLoading, validating: sourceDataValidating, refresh: sourceDataRefresh } = sourceDataContext;
    const { data: configuration, loading: configurationLoading, validating: configurationValidating } = configurationContext;

    const eventStatusContext =  useParticipantSourceDataEventStatuses(participant?.id);
    const { loading: eventStatusesLoading, validating: eventStatusesValidating, refresh: eventStatusRefresh } = eventStatusContext;

    const formStatusContext =  useParticipantSourceDataFormStatuses(participant?.id);
    const { loading: formStatusesLoading, validating: formStatusesValidating, refresh: formStatusRefresh } = formStatusContext;

    const notesContext = useParticipantNotesAndQueries(participant?.id);
    const { loading: notesLoading, validating: notesValidating, refresh: notesRefresh } = notesContext;

    const findingsContext = useParticipantSourceDataFindings(participant?.id);
    const { loading: findingsLoading, validating: findingsValidating, refresh: findingsRefresh } = findingsContext;

    const [refreshing, setRefreshing] = useState(false);

    const { studyEventOid, studyEventRepeat, caseReportFormOid, trialOid, institutionCode, studyNumber, itemName } = useParams();

    const contextEvent = useMemo(() => {
        const typedEventRepeat = studyEventRepeat ? +studyEventRepeat : undefined;

        if(!studyEventOid || !typedEventRepeat || Number.isNaN(typedEventRepeat)) {
            return undefined;
        }
        return sourceData?.events?.[studyEventOid]?.[typedEventRepeat];
    }, [sourceData, studyEventOid, studyEventRepeat]);

    const debouncedSourceDataRefresh = useCallback(debounce(async () => {
        await sourceDataRefresh();
    }, 500, { trailing: true, leading: false}), [sourceDataRefresh]);


    const afterUpdateStatus = useCallback(async (event: UpdateStatusEvent) => {
        if (event.trialId == trial?.id &&
            event.participantId == participant?.id &&
            event.type === UpdateStatusEventType.Complete) {
                const promises: Promise<any>[] = [];

                if (
                    event.dataType === UpdateStatusDataType.Reviews ||
                    event.dataType === UpdateStatusDataType.Verifications ||
                    event.dataType === UpdateStatusDataType.ReviewsAndVerifications ||
                    event.dataType === UpdateStatusDataType.All ||
                    event.dataType === UpdateStatusDataType.NotApplicable ||
                    event.dataType === UpdateStatusDataType.ReviewsAndNotApplicable ||
                    event.dataType === UpdateStatusDataType.VerificationsAndNotApplicable ||
                    event.dataType === UpdateStatusDataType.ReviewsVerificationsAndNotApplicable
                )
                {
                    promises.push(eventStatusRefresh() as any);
                    promises.push(formStatusRefresh() as any);
                }
                
                if (event.dataType === UpdateStatusDataType.All)
                {
                    promises.push(debouncedSourceDataRefresh() as any);
                }

                await Promise.all(promises);
                setRefreshing(false);
        }
    }, [eventStatusRefresh, formStatusRefresh, setRefreshing, debouncedSourceDataRefresh, trial, participant]);


    const onQueueUpdate = useCallback(async () => {
        setRefreshing(true);
    }, [ setRefreshing]);

    const {processing, queueUpdate, error} = useStatusUpdate(
        onQueueUpdate,
        afterUpdateStatus
    );

    const queueStatusUpdate = useCallback(async () => {
        setRefreshing(true);

        await queueUpdate({ 
            participantId: participant?.id
        });
    }, [queueUpdate, participant?.id, setRefreshing]);

    const sourceDataFormContext = useMemo(() => {
        return {
            updating: processing || refreshing,
            queueUpdate: queueStatusUpdate
        }
    }, [queueStatusUpdate, processing, refreshing])

    const loading = configurationLoading || allocationLoading || sourceDataLoading || eventStatusesLoading || formStatusesLoading;
    const validating = configurationValidating || allocationValidating || sourceDataValidating || eventStatusesValidating || formStatusesValidating || processing || refreshing || notesLoading || notesValidating || findingsLoading || findingsValidating;
    const { showLayout } = useContext(ShowLayoutContext);

    const [formSettings, setFormSettings] = useLocalStorage<ISourceDataFormSettings>('source-data-form-settings', DefaultSourceDataFormSettings)

    const formSettingsContext = useMemo(() => {
        return {
            settings: formSettings ?? DefaultSourceDataFormSettings,
            setSettings: setFormSettings as any
        }
    }, [formSettings, setFormSettings]);

    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    const [editEventDialogOpen, setEditEventDialogOpen] = useState(false);
    const [deleteEventDialogOpen, setDeleteEventDialogOpen] = useState(false);

    const {data: canDeleteEvent, loading: canDeleteEventLoading, validating: canDeleteEventValidating } = useDeleteSourceDataEvent(contextEvent?.id);    
    const { loading: siteLoading } = useContext(LoadingContext)
    const navigate = useNavigate();

    return (
        <>
            <SetLinks createTrialLink={ParticipantTrialCreateLink} createInstitutionLink={ParticipantInstitutionCreateLink} />
            <IsLoading name="participant-source-data-page" loading={loading} validating={validating} />
            <ParticipantSourceDataFindingsContext.Provider value={findingsContext}>
                <ParticipantNotesAndQueriesContext.Provider value={notesContext}>
                    <SourceDataContext.Provider value={sourceDataContext}>
                        <SourceDataFormContext.Provider value={sourceDataFormContext}>
                            <ConfigurationSourceDataContext.Provider value={configurationContext}>
                                <ParticipantSourceDataEventStatusesContext.Provider value={eventStatusContext}>
                                    <ParticipantSourceDataFormStatusesContext.Provider value={formStatusContext}>
                                        <SourceDataFormSettingsContext.Provider value={formSettingsContext}>
                                            {
                                                !AppSettings.showLayout && (siteLoading || loading) && (
                                                    <Box
                                                        sx={{
                                                            position: 'fixed',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <CircularProgress size={80} />
                                                    </Box>
                                                )
                                            }
                                            <Show show={!siteLoading && !loading}>
                                                <ParticipantSourceDataMessages />
                                                <TitleBox>
                                                    <Box>

                                                        <Typography variant="h1">
                                                            {participant?.studyNumber}
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                fontSize: '2rem'
                                                            }}
                                                        >
                                                            Allocation: {configuration?.configuration?.name}
                                                        </Box>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 2
                                                        }}
                                                    >
                                                        {
                                                            !studyEventOid && !studyEventRepeat && !caseReportFormOid && !itemName && (
                                                                <OpenClinicaParticipantLink />
                                                            )
                                                        }
                                                        {
                                                            showLayout && studyEventOid && caseReportFormOid && !itemName && (
                                                                <OpenClinicaCrfLink />
                                                            )
                                                        }
                                                        {
                                                            showLayout && studyEventOid && caseReportFormOid && !itemName && (
                                                                <StatusListItemTooltip 
                                                                    title={
                                                                        <>
                                                                            <strong>Grid View</strong>
                                                                        </>
                                                                    }
                                                                >
                                                                    <Paper>
                                                                        <Button
                                                                            color="inherit"
                                                                            component={Link}
                                                                            to={`/${trialOid}${institutionCode ? `/institutions/${institutionCode}` : ''}/participants/${studyNumber}/forms/${caseReportFormOid}`}
                                                                            sx={{
                                                                                minWidth: 0
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon icon={AppSettings.icons.itemGrid} size="lg"  />
                                                                        </Button>
                                                                    </Paper>
                                                                </StatusListItemTooltip>
                                                            )
                                                        }
                                                        {
                                                            studyEventOid && caseReportFormOid && !itemName && (
                                                                <StatusListItemTooltip 
                                                                    title={
                                                                        <>
                                                                            <strong>Form Settings</strong>
                                                                        </>
                                                                    }
                                                                >
                                                                    <Paper>
                                                                        <Button
                                                                            color="inherit"
                                                                            sx={{
                                                                                minWidth: 0
                                                                            }}
                                                                            onClick={() => setSettingsDialogOpen(true)}
                                                                        >
                                                                            <FontAwesomeIcon icon={AppSettings.icons.configuration} size="lg"  />
                                                                        </Button>
                                                                    </Paper>
                                                                </StatusListItemTooltip>
                                                            )
                                                        }
                                                        {
                                                            studyEventOid && studyEventRepeat && !caseReportFormOid && !itemName && (
                                                                <OpenClinicaEventLink />
                                                            )
                                                        }
                                                        {
                                                            studyEventOid && studyEventRepeat && !caseReportFormOid && !itemName && AppSettings.allowEventDelete && (
                                                                <StatusListItemTooltip 
                                                                    title={
                                                                        canDeleteEvent ?
                                                                            <>
                                                                                <strong>Delete OpenClinica Event</strong>
                                                                                <p>
                                                                                    Deleting the event will remove the event and all associated data from OpenClinica, resetting the event to its initial state.
                                                                                </p>
                                                                                <p>
                                                                                    This action cannot be undone.
                                                                                </p>
                                                                            </> :
                                                                            <>
                                                                                <strong>
                                                                                    Delete OpenClinica Event
                                                                                </strong>
                                                                                <p>
                                                                                    This event cannot be deleted due to one of the following reasons:
                                                                                </p>
                                                                                <ul>
                                                                                    <li>
                                                                                        The event is not in one of the valid states:
                                                                                        <ul>
                                                                                            <li>Scheduled</li>
                                                                                            <li>Data Entry Started</li>
                                                                                        </ul>
                                                                                    </li>
                                                                                    <li>
                                                                                        One of the associated event discrepancy notes is not in one of the valid states:
                                                                                        <ul>
                                                                                            <li>Closed</li>
                                                                                            <li>Not Applicable</li>
                                                                                        </ul>
                                                                                    </li>
                                                                                    <li>
                                                                                        One of the associated case report forms is not in one of the valid states:
                                                                                        <ul>
                                                                                            <li>Not Started</li>
                                                                                            <li>Initial Data Entry</li>
                                                                                            <li>Invalid</li>
                                                                                            <li>Deleted</li>
                                                                                            <li>Automatically Deleted</li>
                                                                                        </ul>
                                                                                    </li>
                                                                                    <li>
                                                                                        One of the associated case report form discrepancy notes is not in one of the valid states:
                                                                                        <ul>
                                                                                            <li>Closed</li>
                                                                                            <li>Not Applicable</li>
                                                                                        </ul>
                                                                                    </li>
                                                                                    <li>
                                                                                        One of the associated case report forms contains data.
                                                                                    </li>
                                                                                </ul>
                                                                            </>
                                                                    }
                                                                >
                                                                    <Paper>
                                                                        <LoadingButton
                                                                            color="inherit"
                                                                            sx={{
                                                                                minWidth: 0
                                                                            }}
                                                                            disabled={!canDeleteEvent || canDeleteEventLoading || canDeleteEventValidating}
                                                                            onClick={() => setDeleteEventDialogOpen(true)}
                                                                            loading={canDeleteEventLoading || canDeleteEventValidating}
                                                                        >
                                                                            <FontAwesomeIcon icon={AppSettings.icons.delete} size="lg"  />
                                                                        </LoadingButton>
                                                                    </Paper>
                                                                </StatusListItemTooltip>
                                                            )
                                                        }
                                                        {
                                                            studyEventOid && studyEventRepeat && !caseReportFormOid && !itemName && (
                                                                <StatusListItemTooltip 
                                                                    title={
                                                                        <>
                                                                            <strong>Update OpenClinica Event</strong>
                                                                        </>
                                                                    }
                                                                >
                                                                    <Paper>
                                                                        <Button
                                                                            color="inherit"
                                                                            sx={{
                                                                                minWidth: 0
                                                                            }}
                                                                            onClick={() => setEditEventDialogOpen(true)}
                                                                        >
                                                                            <FontAwesomeIcon icon={AppSettings.icons.edit} size="lg"  />
                                                                        </Button>
                                                                    </Paper>
                                                                </StatusListItemTooltip>
                                                            )
                                                        }
                                                        <StatusListItemTooltip 
                                                            title={
                                                                <>
                                                                    <strong>Data Last Updated</strong> <br />
                                                                    {sourceData?.date && DateTime.fromISO(sourceData.date).toFormat('dd/MM/yyyy tt')}
                                                                </>
                                                            }
                                                        >
                                                            <Paper>
                                                                <LoadingButton
                                                                    color="inherit"
                                                                    loading={loading || validating || refreshing}
                                                                    onClick={() => {
                                                                        queueStatusUpdate()
                                                                    }}
                                                                    sx={{
                                                                        minWidth: 0
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={AppSettings.icons.refresh} size="lg"  />
                                                                </LoadingButton>
                                                            </Paper>
                                                        </StatusListItemTooltip>
                                                    </Box>
                                                </TitleBox>
                                                <Outlet />
                                                {
                                                    (!!studyEventOid || caseReportFormOid) && showLayout && participant?.status !== ConfigurationStatus.Disabled && (
                                                        <SourceDataNavigation />
                                                    )
                                                }
                                                {
                                                    studyEventOid && caseReportFormOid && !itemName && (
                                                        <SourceDataFormSettingsDialog 
                                                            formData={formSettings}
                                                            onClose={() => {
                                                                setSettingsDialogOpen(false);
                                                            }}
                                                            onSave={async (data) => {
                                                                setFormSettings(data);
                                                            }}
                                                            open={settingsDialogOpen}
                                                        />
                                                    )
                                                }
                                                {
                                                    studyEventOid && studyEventRepeat && !caseReportFormOid && !itemName && (
                                                        <SourceDataEventEditDialog 
                                                            trialId={trial?.id}
                                                            masterGroupId={participant?.masterGroupId}
                                                            coordinatingGroupId={participant?.coordinatingGroupId}
                                                            institutionId={participant?.institutionId}
                                                            participantId={participant?.id}
                                                            studyEventOid={studyEventOid}
                                                            studyEventRepeat={+studyEventRepeat}
                                                            sourceDataEvent={sourceData?.events?.[studyEventOid]?.[+studyEventRepeat]}
                                                            onClose={() => {
                                                                setEditEventDialogOpen(false);
                                                            }}
                                                            onSave={async () => {
                                                                //await sourceDataRefresh()
                                                            }}
                                                            open={editEventDialogOpen}
                                                        />
                                                    )
                                                }
                                                {
                                                    studyEventOid && studyEventRepeat && !caseReportFormOid && !itemName && AppSettings.allowEventDelete && (
                                                        <SourceDataEventDeleteDialog
                                                            open={deleteEventDialogOpen}
                                                            handleClose={async (success) => {
                                                                setDeleteEventDialogOpen(false);

                                                                if (success) {
                                                                    await sourceDataRefresh();
                                                                    navigate(`/${trialOid}${institutionCode ? `/institutions/${institutionCode}` : ''}/participants/${studyNumber}`);
                                                                }
                                                            }}
                                                            sourceDataEvent={sourceData?.events?.[studyEventOid]?.[+studyEventRepeat]}
                                                        />
                                                    )
                                                }
                                            </Show>
                                        </SourceDataFormSettingsContext.Provider>
                                    </ParticipantSourceDataFormStatusesContext.Provider>
                                </ParticipantSourceDataEventStatusesContext.Provider>
                            </ConfigurationSourceDataContext.Provider>
                        </SourceDataFormContext.Provider>
                    </SourceDataContext.Provider>
                </ParticipantNotesAndQueriesContext.Provider>
            </ParticipantSourceDataFindingsContext.Provider>
        </>
    )
};

export default ParticipantSourceData;